import type { NormalizedProduct, SupplierId } from '../types'
import { ALL_SUPPLIER_IDS } from '../connectors/types'

const BRANDS = [
  'Intel',
  'AMD',
  'NVIDIA',
  'Samsung',
  'Kingston',
  'Corsair',
  'Logitech',
  'Dell',
  'Lenovo',
  'ASUS',
  'MSI',
  'Gigabyte',
  'WD',
  'Seagate',
  'Apple',
  'TP-Link',
  'Huawei',
  'Xiaomi',
] as const

const CATEGORIES = [
  'Processador',
  'Placa de Vídeo',
  'Memória RAM',
  'SSD',
  'HD',
  'Monitor',
  'Notebook',
  'Teclado',
  'Mouse',
  'Fonte',
  'Gabinete',
  'Placa-mãe',
  'Headset',
  'Roteador',
  'Cooler',
] as const

const MODELS = [
  'Pro',
  'Plus',
  'Ultra',
  'Gaming',
  'Office',
  'RGB',
  'XT',
  'Max',
  'Lite',
  'Elite',
] as const

let catalogCache: NormalizedProduct[] | null = null

/** Deterministic PRNG for stable mock catalog across reloads. */
function mulberry32(seed: number) {
  return () => {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function pick<T>(rand: () => number, list: readonly T[]): T {
  return list[Math.floor(rand() * list.length)]!
}

/**
 * Generates exactly 500 mock products distributed across all suppliers.
 */
export function createMockCatalog(total = 500): NormalizedProduct[] {
  const rand = mulberry32(20260724)
  const products: NormalizedProduct[] = []

  for (let i = 0; i < total; i++) {
    const supplier = ALL_SUPPLIER_IDS[i % ALL_SUPPLIER_IDS.length]!
    const brand = pick(rand, BRANDS)
    const category = pick(rand, CATEGORIES)
    const model = `${pick(rand, MODELS)}-${1000 + i}`
    const sku = `SKU-${supplier.slice(0, 3).toUpperCase()}-${String(i + 1).padStart(4, '0')}`
    const ean = `789${String(100000000 + i).slice(0, 10)}`
    const purchase = round2(80 + rand() * 4500)
    const pix = round2(purchase * (1.02 + rand() * 0.18))
    const card = round2(pix * (1.05 + rand() * 0.12))
    const stock = Math.floor(rand() * 120)
    const delivery = 1 + Math.floor(rand() * 15)
    const installmentsCount = [3, 6, 10, 12][Math.floor(rand() * 4)]!

    products.push({
      id: `${supplier}-${i + 1}`,
      supplier,
      description: `${category} ${brand} ${model}`,
      brand,
      model,
      sku,
      ean,
      purchasePrice: purchase,
      pixPrice: pix,
      cardPrice: card,
      installments: `${installmentsCount}x de R$ ${round2(card / installmentsCount).toFixed(2)}`,
      stock,
      estimatedDelivery: delivery,
      productUrl: `https://mock.masterhub.local/${supplier}/${sku}`,
      imageUrl: `https://placehold.co/96x96/22c55e/052e16?text=${encodeURIComponent(brand.slice(0, 3))}`,
      currency: 'BRL',
      lastUpdate: new Date(Date.UTC(2026, 6, 1 + (i % 22))).toISOString(),
    })
  }

  return products
}

export function getMockCatalog(): NormalizedProduct[] {
  if (!catalogCache) {
    catalogCache = createMockCatalog(500)
  }
  return catalogCache
}

export function getMockBrands(): string[] {
  return [...new Set(getMockCatalog().map((p) => p.brand))].sort()
}

export function getProductsBySupplier(supplier: SupplierId): NormalizedProduct[] {
  return getMockCatalog().filter((p) => p.supplier === supplier)
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** Test helper — reset cache between suites if needed. */
export function resetMockCatalogCache(): void {
  catalogCache = null
}
